// src/services/medicineRequestService.js
import apiClient from './apiClient';
import { addToPatientInventory } from './patientMedicineInventoryService';

/**
 * Medicine Request Workflow Service
 * Handles complete medicine request lifecycle:
 * REQUESTED → APPROVED/REJECTED → ISSUED → RECEIVED → DISPENSED → RETURNED
 */

// Create a new medicine request (Staff/Nurse)
export const createMedicineRequest = async (requestData) => {
  try {
    // apiClient will automatically add organizationId via interceptor
    const response = await apiClient.post('/medicine-requests', requestData);
    return response.data;
  } catch (error) {
    console.error('Error creating medicine request:', error);
    throw error;
  }
};

// Get all medicine requests
export const getAllMedicineRequests = async () => {
  try {
    // apiClient will automatically add organizationId as query param via interceptor
    // If user has no organizationId, backend should use base table
    const response = await apiClient.get('/medicine-requests');
    return response.data;
  } catch (error) {
    console.error('Error fetching medicine requests:', error);
    throw error;
  }
};

// Get requests by status (for filtering)
export const getRequestsByStatus = async (status) => {
  try {
    // apiClient will automatically add organizationId as query param via interceptor
    // If user has no organizationId, backend should use base table
    const response = await apiClient.get(`/medicine-requests/status/${status}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching requests with status ${status}:`, error);
    throw error;
  }
};

// Get requests by patient ID
export const getRequestsByPatientId = async (patientId) => {
  try {
    // apiClient will automatically add organizationId as query param via interceptor
    const response = await apiClient.get(`/medicine-requests/patient/${patientId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching requests for patient ${patientId}:`, error);
    throw error;
  }
};

// Get requests by admission ID
export const getRequestsByAdmissionId = async (admissionId) => {
  try {
    // apiClient will automatically add organizationId as query param via interceptor
    const response = await apiClient.get(`/medicine-requests/admission/${admissionId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching requests for admission ${admissionId}:`, error);
    throw error;
  }
};

// Get a specific medicine request by ID
export const getMedicineRequestById = async (requestId) => {
  try {
    // apiClient will automatically add organizationId as query param via interceptor
    const response = await apiClient.get(`/medicine-requests/${requestId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching medicine request ${requestId}:`, error);
    throw error;
  }
};

// Approve a medicine request (Pharmacy Admin)
export const approveMedicineRequest = async (requestId, approvalData) => {
  try {
    // Ensure approvalData is clean and properly formatted
    // Remove any null/undefined values and ensure proper types
    const cleanApprovalData = {
      approvedByUserId: approvalData.approvedByUserId ? parseInt(approvalData.approvedByUserId) : 0,
      approvedByName: approvalData.approvedByName || "Unknown",
      approvedDate: approvalData.approvedDate || new Date().toISOString(),
    };
    
    // Remove organizationId from body if it exists (apiClient will add it as query param)
    const { organizationId, ...finalApprovalData } = cleanApprovalData;
    
    // apiClient will automatically add organizationId as query parameter via interceptor
    // (not in body, as MedicineRequestDTO doesn't accept it in request body)
    const response = await apiClient.put(
      `/medicine-requests/${requestId}/approve`,
      finalApprovalData
    );
    return response.data;
  } catch (error) {
    console.error(`Error approving request ${requestId}:`, error);
    console.error(`Approval data sent:`, approvalData);
    if (error.response) {
      console.error(`Backend error response:`, error.response.data);
    }
    throw error;
  }
};

// Reject a medicine request (Pharmacy Admin)
export const rejectMedicineRequest = async (requestId, rejectionData) => {
  try {
    // apiClient will automatically add organizationId via interceptor
    const response = await apiClient.put(
      `/medicine-requests/${requestId}/reject`,
      rejectionData
    );
    return response.data;
  } catch (error) {
    console.error(`Error rejecting request ${requestId}:`, error);
    throw error;
  }
};

// Issue medicine for an approved request (Pharmacy Admin)
export const issueMedicineRequest = async (requestId, issueData) => {
  try {
    // apiClient will automatically add organizationId via interceptor
    const response = await apiClient.put(
      `/medicine-requests/${requestId}/issue`,
      issueData
    );
    
    // Return the response data which now contains inventoryInfo
    return response.data;
    
  } catch (error) {
    console.error(`Error issuing medicine for request ${requestId}:`, error);
    throw error;
  }
};

// Mark medicine as received (Staff/Nurse)
export const receiveMedicineRequest = async (requestId, receiveData) => {
  try {
    // apiClient will automatically add organizationId via interceptor
    const response = await apiClient.put(
      `/medicine-requests/${requestId}/receive`,
      receiveData
    );
    return response.data;
  } catch (error) {
    console.error(`Error receiving medicine for request ${requestId}:`, error);
    throw error;
  }
};

// Mark medicine as dispensed to patient (Staff/Nurse)
export const dispenseMedicineRequest = async (requestId, dispenseData) => {
  try {
    // apiClient will automatically add organizationId via interceptor
    const response = await apiClient.put(
      `/medicine-requests/${requestId}/dispense`,
      dispenseData
    );
    return response.data;
  } catch (error) {
    console.error(`Error dispensing medicine for request ${requestId}:`, error);
    throw error;
  }
};

// Return medicine (Staff/Nurse)
export const returnMedicineRequest = async (requestId, returnData) => {
  try {
    // apiClient will automatically add organizationId via interceptor
    const response = await apiClient.put(
      `/medicine-requests/${requestId}/return`,
      returnData
    );
    return response.data;
  } catch (error) {
    console.error(`Error returning medicine for request ${requestId}:`, error);
    throw error;
  }
};

// Cancel a medicine request (Staff/Nurse - only if not yet approved)
export const cancelMedicineRequest = async (requestId, cancelData) => {
  try {
    // apiClient will automatically add organizationId via interceptor
    const response = await apiClient.put(
      `/medicine-requests/${requestId}/cancel`,
      cancelData
    );
    return response.data;
  } catch (error) {
    console.error(`Error cancelling request ${requestId}:`, error);
    throw error;
  }
};

// Get request statistics (for dashboard)
export const getMedicineRequestStats = async () => {
  try {
    // apiClient will automatically add organizationId as query param via interceptor
    const response = await apiClient.get('/medicine-requests/stats');
    return response.data;
  } catch (error) {
    console.error('Error fetching request statistics:', error);
    throw error;
  }
};

// Get requests for a specific treatment
export const getRequestsByTreatmentId = async (treatmentId) => {
  try {
    // apiClient will automatically add organizationId as query param via interceptor
    const response = await apiClient.get(`/medicine-requests/treatment/${treatmentId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching requests for treatment ${treatmentId}:`, error);
    throw error;
  }
};

